import React from 'react';
import { Search, Lightbulb, Palette, Code, Rocket, Headphones } from 'lucide-react';
import { FadeIn } from './FadeIn';
import { SEO } from './SEO';

export const Process: React.FC = () => {
    const steps = [
        {
            number: '01',
            icon: Search,
            title: 'Discovery & Research',
            description: 'We start by understanding your business, target audience, and goals. Comprehensive market and competitor analysis to identify opportunities.',
            deliverables: ['Business analysis', 'Market research', 'Competitor audit']
        },
        {
            number: '02',
            icon: Lightbulb,
            title: 'Strategy & Planning',
            description: 'Develop a tailored digital strategy that aligns with your business objectives and international expansion plans.',
            deliverables: ['Strategic roadmap', 'Technology stack selection', 'Project timeline']
        },
        {
            number: '03',
            icon: Palette,
            title: 'Design & Prototyping',
            description: 'Create stunning, conversion-focused designs that reflect your brand and resonate with your global audience.',
            deliverables: ['UI/UX design', 'Interactive prototypes', 'Brand guidelines']
        },
        {
            number: '04',
            icon: Code,
            title: 'Development',
            description: 'Build high-performance, scalable websites using modern technologies like React and Next.js, optimized for speed and SEO.',
            deliverables: ['Custom development', 'Mobile optimization', 'SEO implementation']
        },
        {
            number: '05',
            icon: Rocket,
            title: 'Launch & Deployment',
            description: 'Thorough testing, quality assurance, and seamless deployment to ensure your website performs flawlessly from day one.',
            deliverables: ['Quality assurance', 'Performance testing', 'Go-live support']
        },
        {
            number: '06',
            icon: Headphones,
            title: 'Support & Growth',
            description: 'Ongoing maintenance, updates, and optimization to keep your website performing at its best and evolving with your business.',
            deliverables: ['Technical support', 'Performance monitoring', 'Continuous optimization']
        }
    ];

    return (
        <>
            <SEO
                title="Our Process | How We Build Exceptional Websites - Refine Studio"
                description="Discover our proven 6-step web development process: Discovery, Strategy, Design, Development, Launch, and Support. From concept to deployment and beyond."
                keywords="web development process, website development methodology, web design workflow, project management, agile development, website launch process"
                canonical="https://refinestudio.vercel.app/process"
            />
            <section className="py-24 px-6 min-h-screen">
                <div className="max-w-6xl mx-auto">
                    <FadeIn>
                        <div className="text-center mb-20">
                            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Process</h1>
                            <p className="text-xl text-neutral-400 max-w-2xl mx-auto">
                                A proven methodology that delivers exceptional results, from concept to launch
                            </p>
                        </div>
                    </FadeIn>

                    <div className="relative">
                        {/* Connection Line */}
                        <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-blue-500 via-purple-500 to-transparent hidden sm:block" />

                        <div className="space-y-12 md:space-y-24">
                            {steps.map((step, index) => (
                                <FadeIn key={step.number} delay={index * 0.1}>
                                    <div className={`relative flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 md:gap-16 items-center`}>
                                        {/* Content */}
                                        <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                                            <div className="inline-block">
                                                <span className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">
                                                    {step.number}
                                                </span>
                                                <h3 className="text-2xl md:text-3xl font-semibold text-white mb-4 mt-2">{step.title}</h3>
                                                <p className="text-neutral-400 leading-relaxed mb-6 max-w-md">
                                                    {step.description}
                                                </p>
                                                <div className={`flex flex-wrap gap-2 ${index % 2 === 0 ? 'md:justify-end' : 'md:justify-start'}`}>
                                                    {step.deliverables.map((item) => (
                                                        <span key={item} className="px-3 py-1 rounded-full text-xs bg-neutral-800 text-neutral-400 border border-neutral-700">
                                                            {item}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Icon Circle */}
                                        <div className="relative shrink-0">
                                            <div className="w-20 h-20 rounded-full bg-neutral-900 border-2 border-neutral-800 flex items-center justify-center relative z-10 group-hover:border-blue-500 transition-colors">
                                                <div className="p-3 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20">
                                                    <step.icon className="text-blue-400" size={24} />
                                                </div>
                                            </div>
                                            {/* Pulsing background */}
                                            <div className="absolute inset-0 rounded-full bg-blue-500/10 blur-xl animate-pulse" />
                                        </div>

                                        {/* Spacer for alternating layout */}
                                        <div className="flex-1 hidden md:block" />
                                    </div>
                                </FadeIn>
                            ))}
                        </div>
                    </div>

                    <FadeIn delay={0.8}>
                        <div className="mt-20 text-center">
                            <div className="inline-block p-8 rounded-2xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-neutral-800">
                                <p className="text-neutral-300 mb-4">Ready to get started?</p>
                                <p className="text-2xl font-semibold text-white mb-6">Let's discuss your project</p>
                                <a
                                    href="https://wa.me/918208646135"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block px-8 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-full transition-all hover:shadow-lg hover:shadow-green-500/25"
                                >
                                    Chat on WhatsApp
                                </a>
                            </div>
                        </div>
                    </FadeIn>
                </div>
            </section>
        </>
    );
};

export default Process;
